'use client';

import { ReactNode } from 'react';

import {
    Align,
    Column,
    Columns,
    Icon,
    IconName,
    IconSize,
    Text,
    TextLevels,
    TextMarkup,
    TrilogyColor,
    TypographyBold,
} from '@bytel/trilogy-react';

import { InlinePrice } from '@components/inline-price';

import { scrollToElement } from '@helpers/utils';

type StickyItemProps = {
    iconName: IconName;
    offerType: string;
    noOffer?: {
        text: string;
        id: string;
    };
    price?: number;
    order?: number;
    promoLabel?: string;
    isLoading: boolean;
    secondLine?: ReactNode;
};

export default function StickyItem({
    iconName,
    offerType,
    noOffer,
    price,
    order,
    promoLabel,
    isLoading,
    secondLine,
}: Readonly<StickyItemProps>) {
    return (
        <div className='row' style={{ order }}>
            <Columns
                gap={1}
                verticalAlign={!price ? Align.CENTER : undefined}
                mobile
                className='is-spaced-between-mobile'
            >
                <Column className='is-hidden-mobile' narrow>
                    <Icon
                        name={iconName}
                        size={IconSize.SMALL}
                        circled
                        backgroundColor={TrilogyColor.INFO_FADE}
                        color={TrilogyColor.MAIN}
                    />
                </Column>
                <Column narrow>
                    <Text level={TextLevels.TWO} marginless markup={TextMarkup.SPAN} className='is-level-3-mobile'>
                        <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} markup={TextMarkup.SPAN}>
                            {offerType}&nbsp;:{' '}
                        </Text>
                        <Text
                            markup={TextMarkup.SPAN}
                            level={TextLevels.TWO}
                            className={`is-inline is-level-3-mobile ${isLoading ? 'is-loading' : ''} is-hidden-mobile`}
                            marginless
                        >
                            <PriceOrLink
                                offerType={offerType}
                                price={price}
                                promoLabel={promoLabel}
                                noOffer={noOffer}
                            />
                        </Text>
                    </Text>
                    <br />
                    {secondLine}
                </Column>
                <Column narrow className='is-hidden-tablet'>
                    <PriceOrLink offerType={offerType} price={price} promoLabel={promoLabel} noOffer={noOffer} />
                </Column>
            </Columns>
        </div>
    );
}

type PriceOrLinkProps = {
    offerType: string;
    price?: number;
    promoLabel?: string;
    noOffer?: {
        text: string;
        id: string;
    };
};

function PriceOrLink({ offerType, price, promoLabel, noOffer }: Readonly<PriceOrLinkProps>) {
    if (offerType && price) {
        return (
            <>
                {promoLabel && <span>{`(${promoLabel}) `}</span>}
                <InlinePrice
                    level={TextLevels.TWO}
                    className='is-level-3-mobile'
                    amount={price}
                    period='mois'
                    typo={[]}
                />
            </>
        );
    }

    if (noOffer) {
        return (
            <button
                className='button-clear link custom-height'
                onClick={() => {
                    scrollToElement(noOffer.id);
                }}
            >
                {noOffer.text}
            </button>
        );
    }

    return null;
}
